import { Button, Stack } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { DataGridToolbarSelection } from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import ExcelIcon from "@entrilia/shared/icons/fileFormats/ExcelIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DataImportOption } from "../../../api/types/dataImportTypes";
import type {
  ObjectFieldValues,
  ObjectFilter,
  ObjectWithFieldValuesBase,
  SearchObjectsRequest,
} from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import { pageRoutes } from "../../../routes";
import { getReferrerLocationState } from "../../../utilities/navigationHelper";
import ExportWithFiltersButton from "../../common/ExportWithFiltersButton";
import { FilterContextProvider } from "../../common/filters/FilterContextProvider";
import FiltersPanel from "../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../common/filters/filterState";
import type { TableFilter } from "../../common/filters/filterTypes";
import { mapApiObjectFilterToReadonlyTableFilter } from "../../common/filters/objectFilterMapper";
import { mapLookupOptionsToSelectOptions } from "../helpers";
import { getObjectFilterDefinitions, getSearchObjectsFilterPayload } from "../objectFieldFilterDefinitions";
import ObjectsGrid from "./ObjectsGrid";
import ObjectsListContext from "./ObjectsListContext";
import type { ExcelExportConfig } from "./StartObjectsExportConfirmationDialog";
import StartObjectsExportConfirmationDialog from "./StartObjectsExportConfirmationDialog";

const getLookupOptionsForObjectType = withErrorHandling(adminApi.getLookupOptions);

interface Props {
  dataImportOptions: DataImportOption[];
  objectsTitle: string;
  showLogos?: boolean;
  predefinedFiltersName?: string;
  predefinedFilters?: ObjectFilter[];
  getDetailsPath: (id: string) => string;
  detailsNavigationState?: unknown;
  refreshTrigger?: number;
  excelExportConfig?: ExcelExportConfig;
  actionsColumn?: GridColDef<ObjectFieldValues>;
  selectionActions?: ReactNode;
}

type ExportDialogState = { open: false } | { open: true; filters?: ObjectFilter[] };

const ObjectsList = ({
  dataImportOptions,
  objectsTitle,
  showLogos,
  predefinedFiltersName,
  predefinedFilters,
  getDetailsPath,
  detailsNavigationState,
  refreshTrigger,
  excelExportConfig,
  actionsColumn,
  selectionActions,
}: Props) => {
  const { clientCode, dictionaries } = useClientContext();
  const { objectClassDefinition } = useDefinedContext(ObjectsListContext, "ObjectsListContext");
  const location = useLocation();
  const navigate = useNavigate();

  const filterId = useMemo(
    () =>
      `${clientCode}_${objectClassDefinition.objectType}_${predefinedFiltersName ? predefinedFiltersName + "_" : ""}filter_v2`,
    [clientCode, objectClassDefinition.objectType, predefinedFiltersName]
  );

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const [filterState, setFilterState] = useState(() => {
    const { filterDefinitions, columnDefinitions } = getObjectFilterDefinitions(
      objectClassDefinition.fields,
      objectClassDefinition.defaultGridFieldIds,
      objectClassDefinition.showTimestampsInGridByDefault,
      dictionaries
    );

    const predefinedTableFilters = predefinedFilters
      ? (predefinedFilters
          .map((objectFilter) => mapApiObjectFilterToReadonlyTableFilter(objectFilter, objectClassDefinition.fields))
          .filter(Boolean) as TableFilter<ObjectWithFieldValuesBase>[])
      : [];

    return getInitialFilterState(filterId, filterDefinitions, predefinedTableFilters, columnDefinitions);
  });

  const filterPayload = useMemo(() => getSearchObjectsFilterPayload(filterState), [filterState]);

  const getObjects = useCallback(
    (req: SearchObjectsRequest) => adminApi.searchObjects(objectClassDefinition.objectType, req),
    [objectClassDefinition.objectType]
  );

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    getObjects,
    filterPayload
  );

  const prevTriggerRef = useRef(refreshTrigger);

  useEffect(() => {
    if (refreshTrigger !== prevTriggerRef.current) {
      prevTriggerRef.current = refreshTrigger;
      refreshDataSource();
    }
  }, [refreshTrigger, refreshDataSource]);

  const getSelectOptionsForFilter = useCallback(
    async (filterId: string) => {
      const [lookupOptions, error] = await getLookupOptionsForObjectType(objectClassDefinition.objectType);
      if (error) {
        logError(error, `[ObjectsListPage] getLookupOptions for ${objectClassDefinition.objectType}`);
        return [];
      }

      return mapLookupOptionsToSelectOptions(lookupOptions, objectClassDefinition.fields, filterId);
    },
    [objectClassDefinition.fields, objectClassDefinition.objectType]
  );

  const handleImportFromCsv = () => {
    navigate(`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}/${pageRoutes.importCsvData}`, {
      state: {
        ...getReferrerLocationState(location),
        preSelectedObjectType: objectClassDefinition.objectType,
      },
    });
  };

  const hasPermissionsForDataImport = dataImportOptions.some(
    (option) => option.canImport && option.objectType === objectClassDefinition.objectType
  );

  const hasAccessToCsvImport =
    hasPermissionsForDataImport && objectClassDefinition.supportedFeatures.includes("DataImport");

  const [exportDialogState, setExportDialogState] = useState<ExportDialogState>({ open: false });

  const hasSelectionActions = !!selectionActions;

  const gridSelection = useMemo<DataGridToolbarSelection | undefined>(
    () => (hasSelectionActions ? [selectedRowIds, setSelectedRowIds] : undefined),
    [hasSelectionActions, selectedRowIds]
  );

  return (
    <Stack spacing={2} sx={{ height: "100%", px: 3, pb: 1, pt: 2.5 }}>
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        getSelectOptionsForFilter={getSelectOptionsForFilter}
      >
        <FiltersPanel
          recordCount={totalRows ?? 0}
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          hasColumnSelection
          onRefresh={refreshDataSource}
        >
          {hasAccessToCsvImport && (
            <Button
              variant="outlined"
              color="secondary"
              disabled={isLoading}
              startIcon={<ExcelIcon />}
              sx={{ minWidth: "auto", whiteSpace: "nowrap" }}
              onClick={handleImportFromCsv}
            >
              Import from CSV
            </Button>
          )}
          {excelExportConfig && (
            <ExportWithFiltersButton
              disabled={!totalRows || isLoading}
              onExportAllRecords={() => setExportDialogState({ open: true })}
              onExportFilteredRecords={() => setExportDialogState({ open: true, filters: filterPayload.filters })}
            />
          )}
        </FiltersPanel>
        <ObjectsGrid
          apiRef={gridApiRef}
          dataSource={dataSource}
          filterId={filterId}
          fields={objectClassDefinition.fields}
          showLogos={showLogos}
          showDuplicateCaptions={!!objectClassDefinition.duplicateDetection}
          getDetailsPath={getDetailsPath}
          detailsNavigationState={detailsNavigationState}
          selection={gridSelection}
          actionsColumn={actionsColumn}
          selectionActions={selectionActions}
        />
      </FilterContextProvider>
      {exportDialogState.open && excelExportConfig && (
        <StartObjectsExportConfirmationDialog
          objectType={objectClassDefinition.objectType}
          objectsTitle={objectsTitle}
          exportConfig={excelExportConfig}
          filters={exportDialogState.filters}
          onClose={() => setExportDialogState({ open: false })}
        />
      )}
    </Stack>
  );
};

export default ObjectsList;
